import { useEffect, useState } from 'react'

import { CalendarIcon } from '../../../workflows/icons/calendar-icon'
import { CreditScoreIcon } from '../../../workflows/icons/credit-score-icon'
import { FederalIcon } from '../../../workflows/icons/federal-icon'
import { HouseIcon } from '../../../workflows/icons/house-icon'
import { IncomeIcon } from '../../../workflows/icons/income-icon'
import { MailIcon } from '../../../workflows/icons/mail-icon'
import { PiggyBankIcon } from '../../../workflows/icons/piggy-bank-icon'
import { PinIcon } from '../../../workflows/icons/pin-icon'
import { UserIcon } from '../../../workflows/icons/user-icon'
import { WalletIcon } from '../../../workflows/icons/wallet-icon'
import ULCheckBox from '../../components/check-box'
import UploadedFileCard from '../../components/uploaded-file-card'
import { useUploadLeadsContext } from '../../context/upload-leads-context'
import { useUploadLeadsStore } from '../../store/upload-leads-store'

const DEFAULT_FILE_NAME = 'Hubspot leads 2025-2026'
const DEFAULT_FILE_SIZE_BYTES = 480 * 1024
const DEFAULT_BUSINESS_COUNT = '0'
const EMPTY_MAPPED_FIELD_LABEL = 'No mapped fields'
const EMPTY_MAPPED_FIELD_PREVIEW = '-'
const COMMUNICATION_PERMISSIONS = [
  {
    id: 'contact-data-consent',
    copy: "I certify that I have the right to provide these contacts' information to Conrad and that I have obtained any legally required consent or provided any legally required notice for the communication methods enabled below.",
  },
  {
    id: 'email-consent',
    copy: 'These contacts gave permission to receive email communications.',
  },
  {
    id: 'sms-consent',
    copy: [
      'These contacts gave explicit consent to receive text messages, including marketing texts if enabled.',
      'Only enable SMS for contacts who specifically consented to receive text messages.',
    ],
  },
] as const

const UserIconComponent = <UserIcon size={21} className="text-primary" />
const MailIconComponent = <MailIcon size={21} className="text-primary" />
const PinIconComponent = <PinIcon size={21} className="text-primary" />
const HouseIconComponent = <HouseIcon size={21} className="text-primary" />
const WalletIconComponent = <WalletIcon size={21} className="text-primary" />
const CalendarIconComponent = <CalendarIcon size={21} className="text-primary" />
const FederalIconComponent = <FederalIcon size={21} className="text-primary" />
const PiggyBankIconComponent = <PiggyBankIcon size={21} className="text-primary" />
const CreditScoreIconComponent = <CreditScoreIcon size={21} className="text-primary" />
const IncomeIconComponent = <IncomeIcon size={21} className="text-primary" />

const iconMap: Record<string, React.ReactNode | undefined> = {
  'Client First Name': UserIconComponent,
  'Client Last Name': UserIconComponent,
  'Client Email': MailIconComponent,
  'Client Phone': UserIconComponent,
  'Assigned User ID': UserIconComponent,
  'Assigned User Email': MailIconComponent,
  'Assigned User Phone': UserIconComponent,
  'Property Address': PinIconComponent,
  'Property Unit': PinIconComponent,
  'Property City': PinIconComponent,
  'Property State': PinIconComponent,
  'Property Zip': PinIconComponent,
  'Desired Location': PinIconComponent,
  'Desired City': PinIconComponent,
  'Desired State': PinIconComponent,
  'Desired Zip': PinIconComponent,
  'Property Type': HouseIconComponent,
  Bedrooms: HouseIconComponent,
  Bathrooms: HouseIconComponent,
  Price: WalletIconComponent,
  'Desired Closing Date': CalendarIconComponent,
  Closed: CalendarIconComponent,
  'Closed At': CalendarIconComponent,
  Timeframe: CalendarIconComponent,
  'Loan Purpose': FederalIconComponent,
  'Loan Type': FederalIconComponent,
  'Down Payment': PiggyBankIconComponent,
  'External Loan Identifier': FederalIconComponent,
  'External Loan Source': FederalIconComponent,
  'Approved Loan Amount': FederalIconComponent,
  'Estimated Property Value': IncomeIconComponent,
  'Loan Term': CalendarIconComponent,
  'Mortgage Rate': FederalIconComponent,
  'Residence Type': HouseIconComponent,
  'Credit Score': CreditScoreIconComponent,
  'Credit Score Estimate': CreditScoreIconComponent,
  'Original Purchase Price': WalletIconComponent,
}

interface MappedFieldRow {
  icon?: React.ReactNode
  label: string
  preview: string
}

export default function MappedFieldsStep() {
  const uploadedFile = useUploadLeadsStore((state) => state.formData.uploadedFile)
  const { handleGoToStep, setFooterConfig } = useUploadLeadsContext()
  const reset = useUploadLeadsStore((state) => state.reset)
  const [enabledPermissions, setEnabledPermissions] = useState<Record<string, boolean>>({})

  const previewData = uploadedFile?.previewData
  const fileName = uploadedFile?.name ?? DEFAULT_FILE_NAME
  const fileSize = uploadedFile?.size ?? DEFAULT_FILE_SIZE_BYTES
  const businessesCount = previewData?.summary.totalRows
  const businessesCountLabel = businessesCount?.toLocaleString() ?? DEFAULT_BUSINESS_COUNT
  const mappedFields = (previewData?.mappings ?? []).filter((mapping) => mapping.status === 'mapped')
  const mappedFieldRows: MappedFieldRow[] =
    mappedFields.length > 0
      ? mappedFields.map((mapping) => ({
          icon: iconMap[mapping.clientField],
          label: mapping.clientField,
          preview: mapping.uploadedColumn ?? mapping.matchedColumns[0] ?? EMPTY_MAPPED_FIELD_PREVIEW,
        }))
      : [{ label: EMPTY_MAPPED_FIELD_LABEL, preview: EMPTY_MAPPED_FIELD_PREVIEW }]
  const togglePermission = (permissionId: string) => {
    setEnabledPermissions((currentPermissions) => ({
      ...currentPermissions,
      [permissionId]: !currentPermissions[permissionId],
    }))
  }

  useEffect(() => {
    const isAllPermissionsEnabled =
      Object.keys(enabledPermissions).length === COMMUNICATION_PERMISSIONS.length &&
      Object.values(enabledPermissions).every((permission) => permission)
    setFooterConfig({
      isPrimaryDisabled: !isAllPermissionsEnabled,
      buttonText: 'Confirm mapped fields',
      bottomLeftContent: {
        variant: 'link-button',
        text: 'Re-upload',
        onClick: () => handleGoToStep('upload'),
      },
    })

    return () => {
      setFooterConfig(null)
    }
  }, [handleGoToStep, setFooterConfig, enabledPermissions])

  const handleRemoveFile = () => {
    reset()
    handleGoToStep('upload')
  }

  return (
    <div className="flex size-full px-5">
      <div className="my-auto w-full pb-[100px]">
        <div className="mx-auto flex w-full max-w-[950px] items-start justify-between gap-16">
          <div className="sticky top-[100px] w-full max-w-[390px]">
            <h1 className="text-[36px] font-semibold leading-tight tracking-[-0.36px] text-primary">Review import</h1>
            <p className="mt-2 text-[18px] font-medium leading-[1.6] text-muted">
              You&apos;re about to import {businessesCountLabel} businesses
            </p>

            <UploadedFileCard
              fileName={fileName}
              fileSize={fileSize}
              onRemove={handleRemoveFile}
              className="mt-8 justify-start"
            />

            {/* Communication permissions */}
            <div className="mt-9 w-full">
              <h2 className="text-[18px] font-semibold leading-[1.35] tracking-[-0.18px] text-primary">
                Communication permissions:
              </h2>
              <div className="mt-3 flex flex-col gap-2.5">
                {COMMUNICATION_PERMISSIONS.map((permission) => {
                  const isEnabled = Boolean(enabledPermissions[permission.id])

                  return (
                    <button
                      key={permission.id}
                      type="button"
                      onClick={() => togglePermission(permission.id)}
                      className="flex w-full items-start gap-[15px] text-left"
                    >
                      <ULCheckBox checked={isEnabled} />
                      <div className="w-full max-w-[350px] text-[13px] font-medium leading-[1.6] text-muted">
                        {Array.isArray(permission.copy) ? (
                          <>
                            <p>{permission.copy[0]}</p>
                            <p className="mt-0.5">{permission.copy[1]}</p>
                          </>
                        ) : (
                          permission.copy
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="w-full max-w-[450px]">
            <div className="sticky top-0 bg-white pb-5 pt-[100px]">
              <h2 className="text-[18px] font-semibold leading-[1.35] tracking-[-0.18px] text-primary">
                Mapped fields
              </h2>
              <p className="mt-1 text-[15px] font-medium leading-[1.6] text-muted">
                Your CSV columns have been matched to Conrad fields.
              </p>
            </div>

            <div className="rounded-[15px] bg-accent px-[15px] py-5">
              {mappedFieldRows.map((field, index) => (
                <div key={`${field.label}-${index}`}>
                  <MappedFieldRow icon={field.icon} label={field.label} preview={field.preview} />
                  {index < mappedFieldRows.length - 1 ? (
                    <div className="my-[10px] ml-[56px] border-t border-lightBorder" />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface MappedFieldInterface {
  icon?: React.ReactNode
  label: string
  preview: string
}

function MappedFieldRow({ icon, label, preview }: MappedFieldInterface) {
  return (
    <div className="flex items-center gap-[10px]">
      <div className="flex size-[46px] items-center justify-center rounded-[15px] bg-white">
        {icon ?? <UserIcon size={21} className="text-primary" />}
      </div>
      <div>
        <p className="text-[15px] font-semibold leading-none tracking-[-0.25px] text-primary">{label}</p>
        <p className="mt-1 text-[13px] font-medium leading-none text-muted">Preview: {preview}</p>
      </div>
    </div>
  )
}
